/**
 * Native Mobile App Shell Utilities (Capacitor 6 — iOS & Android)
 *
 * Bridges the React 19 web application with native iOS (TestFlight / App Store)
 * and Android (.APK / Google Play) capabilities while maintaining 100% graceful
 * fallback on standard mobile and desktop browsers.
 */

declare global {
  interface Window {
    Capacitor?: {
      isNativePlatform?: () => boolean;
      getPlatform?: () => 'ios' | 'android' | 'web';
      Plugins?: {
        Haptics?: {
          impact?: (opts: { style: 'LIGHT' | 'MEDIUM' | 'HEAVY' }) => Promise<void>;
          notification?: (opts: { type: 'SUCCESS' | 'WARNING' | 'ERROR' }) => Promise<void>;
          vibrate?: (opts?: { duration?: number }) => Promise<void>;
        };
        Share?: {
          share?: (opts: { title?: string; text?: string; url?: string; dialogTitle?: string }) => Promise<void>;
        };
        PushNotifications?: {
          checkPermissions?: () => Promise<{ receive: 'prompt' | 'granted' | 'denied' }>;
          requestPermissions?: () => Promise<{ receive: 'prompt' | 'granted' | 'denied' }>;
          register?: () => Promise<void>;
          addListener?: (
            eventName: string,
            listenerFunc: (data: any) => void
          ) => Promise<{ remove: () => void }>;
        };
        StatusBar?: {
          setBackgroundColor?: (opts: { color: string }) => Promise<void>;
          setStyle?: (opts: { style: 'DARK' | 'LIGHT' | 'DEFAULT' }) => Promise<void>;
        };
      };
    };
  }
}

const SIMULATE_NATIVE_KEY = 'iiab_simulate_native_app';

/**
 * Returns true when running inside the Capacitor iOS/Android native app container
 * OR when the founder has enabled "Preview Native Mobile App Mode" in browser storage.
 */
export function isNativeMobileShell(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.Capacitor?.isNativePlatform?.()) return true;
  try {
    return window.localStorage.getItem(SIMULATE_NATIVE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Returns the active mobile platform ('ios' | 'android' | 'web').
 */
export function getMobilePlatform(): 'ios' | 'android' | 'web' {
  if (typeof window === 'undefined') return 'web';
  const capPlatform = window.Capacitor?.getPlatform?.();
  if (capPlatform === 'ios' || capPlatform === 'android') {
    return capPlatform;
  }
  if (isNativeMobileShell()) {
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/i.test(ua)) return 'ios';
    return 'android';
  }
  return 'web';
}

/**
 * Enables or disables the browser-based Native Mobile App simulation mode.
 */
export function setSimulatedNativeMobileShell(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (enabled) {
      window.localStorage.setItem(SIMULATE_NATIVE_KEY, 'true');
    } else {
      window.localStorage.removeItem(SIMULATE_NATIVE_KEY);
    }
    window.dispatchEvent(new CustomEvent('iiab-native-mode-changed', { detail: { enabled } }));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Apple App Store Review Guideline 3.1.1 Compliance ("Reader / Companion App Rule"):
 * Inside the native iOS/Android container, direct digital checkout buttons for
 * delegate passes or stalls are hidden so Apple/Google never demand a 30% In-App Purchase cut.
 */
export function isAppStoreReaderMode(): boolean {
  return isNativeMobileShell();
}

/**
 * Triggers native tactile haptic feedback on iOS Taptic Engine / Android Vibrator
 * when scanning a QR badge at the Gate Turnstile, Perks Counter, or Exhibitor Booth.
 */
export async function triggerMobileHaptic(
  type: 'success' | 'warning' | 'error' | 'light' = 'light'
): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    const haptics = window.Capacitor?.Plugins?.Haptics;
    if (haptics) {
      if (type === 'light' && haptics.impact) {
        await haptics.impact({ style: 'LIGHT' });
        return;
      }
      if (haptics.notification) {
        const map: Record<'success' | 'warning' | 'error', 'SUCCESS' | 'WARNING' | 'ERROR'> = {
          success: 'SUCCESS',
          warning: 'WARNING',
          error: 'ERROR',
        };
        await haptics.notification({ type: map[type === 'light' ? 'success' : type] });
        return;
      }
    }

    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      if (type === 'success') navigator.vibrate(40);
      else if (type === 'warning') navigator.vibrate([50, 40, 50]);
      else if (type === 'error') navigator.vibrate([90, 50, 90]);
      else navigator.vibrate(20);
    }
  } catch {
    // Non-blocking haptics fallback
  }
}

/**
 * Invokes the native iOS/Android share sheet (or Web Share API / Clipboard fallback).
 */
export async function shareMobileContent(opts: {
  title: string;
  text: string;
  url?: string;
}): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  try {
    const capShare = window.Capacitor?.Plugins?.Share;
    if (capShare?.share) {
      await capShare.share({
        title: opts.title,
        text: opts.text,
        url: opts.url,
        dialogTitle: opts.title,
      });
      return true;
    }

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      await navigator.share({
        title: opts.title,
        text: opts.text,
        url: opts.url,
      });
      return true;
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(opts.url ? `${opts.text} — ${opts.url}` : opts.text);
      return true;
    }
  } catch {
    // User cancelled share sheet
  }
  return false;
}

/**
 * Requests permission and registers the device for native APNs (iOS) / FCM (Android)
 * push notifications when running inside the Capacitor shell.
 */
export async function registerMobilePushNotifications(
  onTokenReceived: (token: string, platform: 'ios' | 'android' | 'web') => void
): Promise<{ supported: boolean; granted: boolean }> {
  if (typeof window === 'undefined') return { supported: false, granted: false };

  const pushPlugin = window.Capacitor?.Plugins?.PushNotifications;
  if (!pushPlugin) {
    return { supported: false, granted: false };
  }

  try {
    let perm = await pushPlugin.checkPermissions?.();
    if (perm?.receive === 'prompt') {
      perm = await pushPlugin.requestPermissions?.();
    }
    if (perm?.receive !== 'granted') {
      return { supported: true, granted: false };
    }

    await pushPlugin.addListener?.('registration', (token: { value?: string }) => {
      if (token?.value) {
        onTokenReceived(token.value, getMobilePlatform());
      }
    });

    await pushPlugin.register?.();
    return { supported: true, granted: true };
  } catch {
    return { supported: false, granted: false };
  }
}
